import React, { useState, useEffect } from 'react';
import { Input, Tooltip, Empty, Typography, Space, Divider } from 'antd';
import { SearchOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { SparkModalPortal, Modal as SparkModal, sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { getPredictionColor } from '@/shared/utils/helpers';
import { recalculate_score } from '@/features/datasets/api/datasetApi';
import { TaskType } from '@/shared/domain/taskTypes';
import { colors } from './colors';
import { useAuth } from '@/features/auth/AuthContext';

const { Text, Title } = Typography;
const { TextArea } = Input;

const LabelManagementModal = ({ visible, onClose, labelsObject, clientId, llmUsecaseId, displayNames, setDisplayNames, isModalLoading, taskType }) => {
    const [searchValue, setSearchValue] = useState('');
    const [labelDescriptions, setLabelDescriptions] = useState({});
    const [localDisplayNames, setLocalDisplayNames] = useState({});
    const [localDescriptions, setLocalDescriptions] = useState({});
    const { role } = useAuth();

    const labels = taskType === TaskType.NER_WITH_RELATIONS ? labelsObject?.relation_names : taskType === TaskType.NER ? labelsObject?.entity_names : labelsObject?.label_names;

    // Reset form when modal opens
    useEffect(() => {
        if (visible) {
            setSearchValue('');
            fetchLabelDescriptions();
            // Initialize local state with current values
            setLocalDisplayNames({ ...displayNames });
        }
    }, [visible]);

    // Sync local descriptions when labelDescriptions changes
    useEffect(() => {
        setLocalDescriptions({ ...labelDescriptions });
    }, [labelDescriptions]);

    const fetchDisplayNames = async () => {
        try {
            if (llmUsecaseId) {
                data_from_server_post(
                    "get-display-names",
                    llmUsecaseId,
                    (response) => {
                        setDisplayNames(response || {});
                    }
                );
            }
        } catch (error) {
            console.error("Failed to fetch display names:", error);
        }
    };

    const fetchLabelDescriptions = async () => {
        try {
            if (llmUsecaseId) {
                data_from_server_post(
                    "get-label-descriptions",
                    llmUsecaseId,
                    (response) => {
                        setLabelDescriptions(response || {});
                    }
                );
            }
        } catch (error) {
            console.error("Failed to fetch label descriptions:", error);
        }
    };

    const handleDisplayNameChange = (label, value) => {
        setLocalDisplayNames(prev => ({ ...prev, [label]: value }));
    };

    const handleDescriptionChange = (label, value) => {
        setLocalDescriptions(prev => ({ ...prev, [label]: value }));
    };

    const handleSaveChanges = async () => {
        // Save all display names and descriptions
        const savePromises = [];

        for (const label of labels || []) {
            const newDisplayName = localDisplayNames[label] || label;
            const newDescription = localDescriptions[label] || '';

            // Check if display name changed
            if (newDisplayName !== (displayNames[label] || label)) {
                savePromises.push(
                    data_from_server_post(
                        "update-display-name",
                        {
                            llm_usecase_id: llmUsecaseId,
                            label: label,
                            label_display_name: newDisplayName.trim()
                        },
                        () => {
                            setDisplayNames(prev => ({ ...prev, [label]: newDisplayName.trim() }));
                        }
                    )
                );
            }

            // Check if description changed
            if (newDescription.trim() !== (labelDescriptions[label] || '').trim()) {
                savePromises.push(
                    data_from_server_post(
                        "update-label-description",
                        {
                            llm_usecase_id: llmUsecaseId,
                            label: label,
                            description: newDescription.trim()
                        },
                        () => {
                            setLabelDescriptions(prev => ({ ...prev, [label]: newDescription.trim() }));
                        }
                    )
                );
            }
        }

        try {
            await Promise.all(savePromises);
        } catch (error) {
            sparkToast.error('Failed to update some labels');
        }

        // Call recalculate score
        try {
            if (clientId && llmUsecaseId) {
                recalculate_score(clientId, llmUsecaseId);
            }
        } catch (error) {
            console.error("Error calling recalculate_score:", error);
        }

        onClose();
    };
    const filteredLabels = labels?.filter ? labels?.filter(label =>
        label.toLowerCase().includes(searchValue.toLowerCase()) ||
        (localDisplayNames[label] && localDisplayNames[label].toLowerCase().includes(searchValue.toLowerCase())) ||
        (localDescriptions[label] && localDescriptions[label].toLowerCase().includes(searchValue.toLowerCase()))
    ) : [];

    return (
        <>
            <SparkModalPortal
                open={visible}
                onClose={onClose}
                destroyOnClose
                size="slot"
            >
            <SparkModal
                title="Review Dataset Labels"
                subtitle="Below are the labels extracted from the dataset, edit to view a different label in the Platform. Labels in the dataset file will remain the same."
                showSubtitle={true}
                showScrollBar={false}
                showFooter={true}
                showClose={true}
                primaryLabel="Confirm"
                tertiaryLabel="Cancel"
                primaryDisableWhenReadOnly
                onCloseClick={onClose}
                onPrimaryClick={isModalLoading ? undefined : handleSaveChanges}
                onTertiaryClick={onClose}
            >
                <Space direction="vertical" style={{ width: '100%', marginTop: '20px' }} size="large">
                    <Input
                        placeholder="Search labels"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                        style={{
                            marginBottom: '12px',
                            borderRadius: '10px',
                            height: '42px',
                            paddingLeft: '12px',
                        }}
                        allowClear
                    />

                    {filteredLabels?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
                            {filteredLabels.map((label, index) => (
                                <React.Fragment key={label}>
                                    <div>
                                        {/* Label extracted and Display Label row */}
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '16px',
                                            marginBottom: '16px'
                                        }}>
                                            <div>
                                                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <div style={{ fontSize: '13px', color: '#595959' }}>
                                                        Label extracted
                                                    </div>
                                                    <Tooltip title="The label as it appears in your dataset">
                                                        <QuestionCircleOutlined style={{ fontSize: '14px', color: '#999' }} />
                                                    </Tooltip>
                                                </div>
                                                <Input
                                                    value={label}
                                                    disabled
                                                    style={{
                                                        borderRadius: '8px',
                                                        height: '40px',
                                                        backgroundColor: '#f5f5f5',
                                                        color: '#666',
                                                        cursor: 'not-allowed'
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <div style={{ fontSize: '13px', color: '#595959' }}>
                                                        Display Label
                                                    </div>
                                                    <Tooltip title="How this label will be displayed in the platform">
                                                        <QuestionCircleOutlined style={{ fontSize: '14px', color: '#999' }} />
                                                    </Tooltip>
                                                </div>
                                                <Input
                                                    value={localDisplayNames[label] !== undefined ? localDisplayNames[label] : label}
                                                    onChange={(e) => handleDisplayNameChange(label, e.target.value)}
                                                    placeholder={label}
                                                    disabled={role === 'Viewer'}
                                                    style={{
                                                        borderRadius: '8px',
                                                        height: '40px',
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Label Description */}
                                        <div>
                                            <div style={{ marginBottom: '8px' }}>
                                                <div style={{ fontSize: '13px', color: '#595959' }}>
                                                    Label Description (optional)
                                                </div>
                                            </div>
                                            <TextArea
                                                value={localDescriptions[label] || ''}
                                                onChange={(e) => handleDescriptionChange(label, e.target.value)}
                                                placeholder="Add a short note to describe this label (e.g. &quot;Bug report&quot;, &quot;Happy customer&quot;). This is for your reference only and won't affect how the model learns."
                                                autoSize={{ minRows: 2, maxRows: 5 }}
                                                disabled={role === 'Viewer'}
                                                style={{
                                                    borderRadius: '8px',
                                                    padding: '12px',
                                                    fontSize: '13px',
                                                    resize: 'none'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {index < filteredLabels.length - 1 && (
                                        <Divider style={{ margin: '24px 0', borderColor: '#f7f7f7' }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <Empty
                            description={
                                <Text style={{ color: '#bbb' }}>
                                    {searchValue ? 'No matching labels found' : 'No labels available'}
                                </Text>
                            }
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{
                                padding: '40px 0',
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                borderRadius: '12px',
                                border: '1px dashed rgba(255, 255, 255, 0.1)'
                            }}
                        />
                    )}
                </Space>
            </SparkModal>
            </SparkModalPortal>
        </>
    );
};

export default LabelManagementModal;